import LocationProps from "../../types/Location";
import MapStore from "./MapStore";
import MapUtils from "./MapUtils";

class MapSearch {
    utils: MapUtils;
    store: MapStore;
    map: google.maps.Map | undefined;
    selectLocation: (location: LocationProps, isNearest?: number) => void;
    locations: LocationProps[] | undefined;
    searchHits: LocationProps[] | undefined;
    currentSearchHitSelection: number;
    config: { MAX_SEARCH_HITS_DISPLAYED: number };

    constructor(
        map: google.maps.Map | undefined,
        locations: LocationProps[],
        selectLocation: (location: LocationProps, isNearest?: number) => void,
    ) {
        this.utils = new MapUtils();
        this.store = new MapStore();
        this.map = map;
        this.selectLocation = selectLocation;
        this.locations = locations;
        this.searchHits = [];
        this.currentSearchHitSelection = 0;

        this.config = {
            MAX_SEARCH_HITS_DISPLAYED: 5,
        };

        this.addListeners();
    }

    setMap(map: google.maps.Map | undefined) {
        this.map = map;
    }

    setLocations(locations: LocationProps[] | undefined) {
        this.locations = locations;
    }

    addListeners() {
        document.addEventListener("click", () => {
            $("#search .search-results ul").hide();
        });

        document.querySelector("#search")?.addEventListener("focusin", () => {
            $("#search .search-results ul").show();
        });

        document.querySelector("#search")?.addEventListener("click", (e) => {
            e.stopPropagation();
        });

        document
            .querySelector("#search input")
            ?.addEventListener("input", () => {
                this.onSearchInput();
            });
        document
            .querySelector("#search input")
            ?.addEventListener("keydown", (e) => {
                this.onSearchKeydown(e as KeyboardEvent);
            });
        document
            .querySelector("#search .search-button-desktop")
            ?.addEventListener("click", () => {
                this.openSearch();
            });
        document
            .querySelector("#search .search-button-mobile")
            ?.addEventListener("click", () => {
                this.toggleSearch();
            });
    }

    onSearchInput() {
        this.emptySearchResults();
        this.currentSearchHitSelection = 0;
        const searchPhrase =
            $("#search > input").val()?.toString().trim() || "";
        this.updateSearchHits(searchPhrase);
        this.renderSearchResults(searchPhrase);
    }

    onSearchKeydown(event: KeyboardEvent) {
        if (event.key === "Enter") {
            // ENTER
            const $listItems = $(
                "#search .search-results ul > li.search-hit .search-hit-trigger",
            );
            $($listItems[this.currentSearchHitSelection]).trigger("click");
        } else if (event.key === "ArrowUp") {
            // UP
            event.preventDefault();
            this.currentSearchHitSelection--;
            if (this.currentSearchHitSelection < 0)
                this.currentSearchHitSelection = this.searchHits
                    ? this.searchHits.length - 1
                    : 0;
            this.updateSearchHitSelection();
        } else if (event.key === "ArrowDown") {
            // DOWN
            event.preventDefault();
            this.currentSearchHitSelection++;
            if (
                this.searchHits &&
                this.currentSearchHitSelection > this.searchHits.length - 1
            )
                this.currentSearchHitSelection = 0;
            this.updateSearchHitSelection();
        }
    }

    openSearch() {
        $("#search").addClass("open");
        $("#search > input").focus();
    }

    emptySearchResults() {
        $("#search .search-results ul").empty();
    }

    closeSearch() {
        this.emptySearchResults();
        $("#search > input").val("");
        $("#search > input").blur();
        $("#search").removeClass("open");
    }

    toggleSearch() {
        if ($("#search").hasClass("open")) {
            this.closeSearch();
        } else {
            this.openSearch();
        }
    }

    updateSearchHits(searchPhrase: string) {
        const terms = (searchPhrase || "")
            .toLowerCase()
            .trim()
            .split(/\s+/)
            .filter(Boolean);

        if (terms.length === 0) {
            this.searchHits = [];
            return;
        }

        this.searchHits = (this.locations ?? [])
            .filter((store) => {
                const fullText = (
                    (store.Name ?? "") +
                    " " +
                    (store.Street ?? "") +
                    " " +
                    (store.Zip ?? "") +
                    " " +
                    (store.City ?? "")
                ).toLowerCase();

                return terms.every((term) => fullText.includes(term));
            })
            .slice(0, this.config.MAX_SEARCH_HITS_DISPLAYED);
    }

    renderSearchResults(searchPhrase: string) {
        const resultsList = document.querySelector<HTMLUListElement>(
            "#search .search-results ul",
        );
        if (!resultsList) return;

        if (
            this.searchHits &&
            this.searchHits.length == 0 &&
            searchPhrase !== ""
        ) {
            const noResult = document.createElement("li");
            noResult.className = "no-search-hit";
            noResult.textContent = "Din sökning gav inget resultat";
            resultsList.append(noResult);
        } else {
            this.searchHits?.forEach((store, index) => {
                const listItem = document.createElement("li");
                const button = document.createElement("button");
                const name = document.createElement("span");
                const address = document.createElement("span");

                listItem.className = `search-hit${index === this.currentSearchHitSelection ? " active" : ""}`;
                button.className = "search-hit-trigger";
                button.dataset.storeId = store.StoreId;
                name.className = "search-hit-name";
                address.className = "search-hit-address";

                this.appendHighlightedText(name, store.Name, searchPhrase);
                this.appendHighlightedText(
                    address,
                    `${store.Street || ""}, ${store.Zip || ""} ${store.City || ""}`,
                    searchPhrase,
                );

                button.append(name, document.createElement("br"), address);
                listItem.append(button);
                resultsList.append(listItem);
            });
        }

        document
            .querySelectorAll(
                "#search .search-results ul li .search-hit-trigger",
            )
            .forEach((searchResult) => {
                searchResult.addEventListener("click", () => {
                    const event = new CustomEvent("selectLocationFromStoreId", {
                        detail: (searchResult as HTMLElement).dataset.storeId,
                    });
                    window.dispatchEvent(event);
                    this.closeSearch();
                });
            });
    }

    appendHighlightedText(
        element: HTMLElement,
        text: string,
        searchPhrase: string,
    ) {
        const terms = searchPhrase.trim().split(/\s+/).filter(Boolean);
        if (!terms.length) {
            element.textContent = text;
            return;
        }

        const escapedTerms = terms.map((term) =>
            term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        );
        const regex = new RegExp(escapedTerms.join("|"), "gi");
        let lastIndex = 0;

        for (const match of text.matchAll(regex)) {
            element.append(document.createTextNode(text.slice(lastIndex, match.index)));
            const highlight = document.createElement("strong");
            highlight.textContent = match[0];
            element.append(highlight);
            lastIndex = (match.index ?? 0) + match[0].length;
        }

        element.append(document.createTextNode(text.slice(lastIndex)));
    }

    updateSearchHitSelection() {
        const $listItems = $(
            "#search .search-results ul > li.search-hit .search-hit-trigger",
        );
        $listItems.removeClass("active");
        $($listItems[this.currentSearchHitSelection]).addClass("active");
    }
}

export default MapSearch;
